import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useGalleryConfig } from '../context/GalleryConfigContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import Button from '../components/common/Button';

const CustomerLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading, error } = useCustomerAuth();
  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await login(formData.email, formData.password, formData.remember);

      // Navigation will be handled by the useEffect above
    } catch {
      // Error is handled by the context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (!config) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
            Welcome Back
          </h1>
          <p className="text-lg" style={{ color: theme.colors.text.secondary }}>
            Sign in to {config.businessName}
          </p>
        </div>

        {/* Login Form */}
        <div className="p-8 rounded-lg shadow-lg" style={{ backgroundColor: theme.colors.surface }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.colors.text.primary }}
              >
                Email Address
              </label>
              <div className="relative">
                <Mail
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: theme.colors.text.muted }}
                />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                  style={{
                    borderColor: theme.colors.border,
                    backgroundColor: theme.colors.background,
                  }}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.colors.text.primary }}
              >
                Password
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: theme.colors.text.muted }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                  style={{
                    borderColor: theme.colors.border,
                    backgroundColor: theme.colors.background,
                  }}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  {showPassword ? (
                    <EyeOff size={16} style={{ color: theme.colors.text.muted }} />
                  ) : (
                    <Eye size={16} style={{ color: theme.colors.text.muted }} />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleInputChange}
                  className="rounded"
                  style={{ accentColor: theme.colors.primary }}
                />
                <span className="text-sm" style={{ color: theme.colors.text.secondary }}>
                  Remember me
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm hover:underline"
                style={{ color: theme.colors.primary }}
              >
                Forgot password?
              </Link>
            </div>

            {/* Error Message */}
            {error && (
              <div
                className="p-3 rounded-lg text-sm"
                style={{
                  backgroundColor: theme.colors.error + '10',
                  color: theme.colors.error,
                }}
              >
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              loading={isSubmitting || loading}
              disabled={!formData.email || !formData.password}
            >
              Sign In
            </Button>
          </form>

          {/* Register Link */}
          {config.settings.allowGuestPurchases && !config.settings.requireRegistration && (
            <div className="mt-6 text-center">
              <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-medium hover:underline"
                  style={{ color: theme.colors.primary }}
                >
                  Sign up
                </Link>
              </p>
            </div>
          )}

          {/* Guest Checkout Option */}
          {config.settings.allowGuestPurchases && (
            <div className="mt-6 pt-6 border-t" style={{ borderTopColor: theme.colors.border }}>
              <div className="text-center">
                <p className="text-sm mb-3" style={{ color: theme.colors.text.secondary }}>
                  Or continue as guest
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const from = (location.state as any)?.from || '/';
                    navigate(from, { replace: true });
                  }}
                >
                  Continue as Guest
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLoginPage;
